/**
 * The T5 plot panel: the sol, season and relief sliders and their live scrub
 * preview, the grid-line and contour toggles, the cell-mark cycle, and the
 * sol/year sweep and flyover buttons.
 */

import { gameClock } from '../../world/clock.ts';
import { TEST_SWITCHES } from '../../urlFlags.ts';
import type { AtlasLive, AtlasState } from '../../atlas';
import type { BodyReadersHandle } from '../bodyReaders';
import type { CameraRigHandle } from '../cameraRig';
import type { DescentLoadHandle } from '../descentLoad';
import type { InjectsHandle } from '../injects';
import type { OverlayViewHandle } from '../overlayView';
import type { PlotSunHandle } from '../plotSun';
import type { PyramidViewHandle } from '../pyramidView.ts';
import type { SelectionHandle } from '../selection';
import type { SolarScrubHandle } from '../solarScrub.ts';
import type { SubPlotMarkMode } from '../subPlotMark.ts';
import type { ViewClockHandle } from '../viewClock';

export interface PlotControlsDeps {
  readonly overlay: HTMLElement;
  readonly state: AtlasState;
  readonly GRID_STAGES: readonly { readonly label: string }[];
  readonly bodyReaders: BodyReadersHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly plotSun: PlotSunHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly selection: SelectionHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly injects: InjectsHandle;
  /** Written from inside this seam as well as read. */
  readonly viewClockHandle: ViewClockHandle;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands them over through getters. */
  readonly cameraRig: CameraRigHandle;
  readonly overlayView: OverlayViewHandle;
}

/** The panel's own state that outside readers reach through the handle. */
export interface PlotControlsState {
  cellMode: SubPlotMarkMode;
}

export interface PlotControlsHandle {
  syncSolNote(solDayS: number | null): void;
  syncPlotLabels(): void;
  endSolScrub(): void;
  endForecastScrub(): void;
  applyCellMode(): void;
  setPlaying(mode: 'sol' | 'year' | null): void;
  readonly elPlotCtl: HTMLElement;
  readonly pcSol: HTMLInputElement;
  readonly pcLs: HTMLInputElement;
  readonly pcLsRow: HTMLElement;
  readonly pcFly: HTMLButtonElement;
  readonly pcPlayYear: HTMLButtonElement;
  readonly state: PlotControlsState;
}

export function createPlotControls(deps: PlotControlsDeps): PlotControlsHandle {
  const { overlay, state, GRID_STAGES, solarScrub, pyramidView } = deps;
  const { rowById, bodyClimate } = deps.bodyReaders;
  const { applySun } = deps.plotSun;
  const { surveyRings } = deps.descentLoad;
  const { selectMark, hoverMark, applySelection } = deps.selection;
  const { beginPreview } = deps.viewClockHandle;
  const { SCRUB_MOVES_CLOCK } = deps.injects.state;
  const { solar } = solarScrub;

  // ── T5 controls ──────────────────────────────────────────────────────────
  // Built once and toggled, not re-rendered — re-writing innerHTML underneath a
  // slider the player is dragging cancels the drag.
  const elPlotCtl = overlay.querySelector('#atlas-plotctl') as HTMLElement;
  const pcSol = overlay.querySelector('#pc-sol') as HTMLInputElement;
  const pcLs = overlay.querySelector('#pc-ls') as HTMLInputElement;
  const pcLsRow = overlay.querySelector('#pc-ls-row') as HTMLElement;
  // RELIEF is a dev and preview control; production builds fold the row away.
  const pcEx = TEST_SWITCHES ? overlay.querySelector('#pc-ex') as HTMLInputElement : null;
  const pcSolV = overlay.querySelector('#pc-sol-v') as HTMLElement;
  const pcLsV = overlay.querySelector('#pc-ls-v') as HTMLElement;
  const pcExV = TEST_SWITCHES ? overlay.querySelector('#pc-ex-v') as HTMLElement : null;
  const pcSolNote = overlay.querySelector('#pc-sol-note') as HTMLElement;
  const pcFly = overlay.querySelector('#pc-fly') as HTMLButtonElement;
  const pcPlaySol = overlay.querySelector('#pc-play-sol') as HTMLButtonElement;
  const pcPlayYear = overlay.querySelector('#pc-play-year') as HTMLButtonElement;
  const pcOverlay = overlay.querySelector('#pc-overlay') as HTMLButtonElement;
  const pcContour = overlay.querySelector('#pc-contour') as HTMLButtonElement;

  /** A solar day in the unit a reader of that magnitude would use. */
  function formatSolDay(s: number): string {
    return s < 2 * 86400 ? `${(s / 3600).toFixed(2)} h` : `${(s / 86400).toFixed(2)} Earth days`;
  }

  /**
   * What the panel says about the day its clock is counting.
   *
   * The honesty overlay's own job, in the plot panel's credit line: a solar day
   * this repo DERIVED from the row's rotation and orbit is not the same claim
   * as one a mission published, and a row that can supply neither has the SOL
   * scrubber taken away rather than a number invented for it.
   */
  function syncSolNote(solDayS: number | null): void {
    const row = rowById(state.bodyId);
    if (!row) { pcSolNote.classList.add('hidden'); return; }
    if (solDayS === null) {
      pcSolNote.textContent = `${row.display_name} orbits nothing, so no sun returns to a `
        + 'meridian and it has no solar day. The SOL scrubber is not shown.';
    } else if (row.rotation.solar_day_s === null) {
      pcSolNote.textContent = `solar day ${formatSolDay(solDayS)}, derived from this body's own `
        + 'rotation and orbit. No solar day has been published for it.';
    } else {
      pcSolNote.classList.add('hidden');
      return;
    }
    pcSolNote.classList.remove('hidden');
  }

  function syncPlotLabels(): void {
    pcSolV.textContent = solarScrub.solClockLabel(solar.solPhase);
    pcLsV.textContent = `Ls ${solar.lsDeg.toFixed(0)}°`;
    if (TEST_SWITCHES) pcExV!.textContent = `×${solar.exag.toFixed(1)}`;
  }
  pcSol.addEventListener('input', () => {
    // Within the current sol: the slider previews a time of day, not a second
    // time base, and the clock the ship flies on runs on under it.
    deps.viewClockHandle.state.solScrubbing = true;
    beginPreview();
    solarScrub.scrubToSolPhase(Number(pcSol.value) / 1000);
    applySun();
    syncPlotLabels();
    deps.overlayView.renderOverlay();
  });
  function endSolScrub(): void {
    if (!deps.viewClockHandle.state.solScrubbing) return;
    deps.viewClockHandle.state.solScrubbing = false;
    returnToLive();
  }
  // Never `change`: a keyboard step commits `input` and `change` in the same
  // task in Chromium, which would end the preview before the frame draws it.
  // A keyboard preview instead holds until focus leaves the slider.
  pcSol.addEventListener('blur', endSolScrub);
  pcSol.addEventListener('keydown', (e) => { if (e.key === 'Escape') endSolScrub(); });
  pcSol.addEventListener('mouseup', endSolScrub);
  pcSol.addEventListener('touchend', endSolScrub);
  function beginForecastScrub(): void {
    if (deps.viewClockHandle.state.forecasting) return;
    deps.viewClockHandle.state.forecasting = true;
    deps.viewClockHandle.state.forecastAnchorT = gameClock().t;
    beginPreview();
  }
  pcLs.addEventListener('mousedown', beginForecastScrub);
  pcLs.addEventListener('input', () => {
    // A keyboard step arrives with no mousedown before it.
    beginForecastScrub();
    const climate = bodyClimate();
    solarScrub.scrubToLs(Number(pcLs.value));
    // The season changes the insolation integral, so the verdicts genuinely
    // move — a north-facing cell can fall out of "prime" as the sun goes south.
    // No mesh above the SURFACE edge, so the field's own scoring is the whole
    // job there; with one, its overlay texture is painted from the same pass.
    if (deps.live.plot && deps.live.plotField) deps.live.plotSubs = deps.live.plot.measure(surveyRings(deps.live.plotField), solar.lsDeg);
    else if (deps.live.plotField && climate) deps.live.plotField.scoreSubPlots(deps.live.plotSubs, solar.lsDeg, climate);
    if (deps.live.selectedSub) deps.live.selectedSub = deps.live.plotSubs.find((p) => p.id === deps.live.selectedSub!.id) ?? deps.live.selectedSub;
    applySelection();
    applySun();
    syncPlotLabels();
    deps.overlayView.renderOverlay();
  });
  /** Back to the live season when the Ls slider is released. */
  function endForecastScrub(): void {
    if (!deps.viewClockHandle.state.forecasting) return;
    deps.viewClockHandle.state.forecasting = false;
    if (SCRUB_MOVES_CLOCK) gameClock().setInstant(deps.viewClockHandle.state.forecastAnchorT);
    returnToLive();
  }
  /**
   * The view back on the clock the ship flies on, where it stands now, once
   * nothing previews any more: a season or sol drag let go, or a sweep stopped.
   */
  function returnToLive(): void {
    if (deps.viewClockHandle.state.forecasting || deps.viewClockHandle.state.solScrubbing || deps.viewClockHandle.state.playing) return;
    deps.viewClockHandle.state.previewT = null;
    const climate = bodyClimate();
    solarScrub.syncSolar();
    // No mesh above the SURFACE edge, so the field's own scoring is the whole
    // job there; with one, its overlay texture is painted from the same pass.
    if (deps.live.plot && deps.live.plotField) deps.live.plotSubs = deps.live.plot.measure(surveyRings(deps.live.plotField), solar.lsDeg);
    else if (deps.live.plotField && climate) deps.live.plotField.scoreSubPlots(deps.live.plotSubs, solar.lsDeg, climate);
    if (deps.live.selectedSub) deps.live.selectedSub = deps.live.plotSubs.find((p) => p.id === deps.live.selectedSub!.id) ?? deps.live.selectedSub;
    applySelection();
    applySun();
    syncPlotLabels();
    pcLs.value = String(Math.round(solar.lsDeg));
    pcSol.value = String(Math.round(solar.solPhase * 1000));
    deps.overlayView.renderOverlay();
  }
  // Never `change`, for the same reason as the sol slider above: it would
  // end a keyboard preview in the same task it began in.
  pcLs.addEventListener('blur', endForecastScrub);
  pcLs.addEventListener('keydown', (e) => { if (e.key === 'Escape') endForecastScrub(); });
  pcLs.addEventListener('mouseup', endForecastScrub);
  pcLs.addEventListener('touchend', endForecastScrub);
  if (TEST_SWITCHES) pcEx!.addEventListener('input', () => {
    solar.exag = Number(pcEx!.value) / 10;
    deps.live.plot?.setExaggeration(solar.exag);
    // The slider used to move the plot and not the terrain under it. A tile's
    // relief is baked into its positions, so this is a rebuild, spread over
    // the same per-frame mesh budget a level change is - and coalesced, since a
    // drag fires this on every event and the mesh reads solar.exag when built.
    pyramidView.tileRemeshAll = true;
    // Every other control routes through applySun, which wakes the renderer.
    // This one does not, and under render-on-demand that means dragging the
    // relief slider would change a uniform nobody ever draws.
    deps.live.needsRender = true;
    syncPlotLabels();
    deps.overlayView.renderOverlay();
  });
  function syncGridButton(): void {
    const g = GRID_STAGES[solar.overlay]!;
    pcOverlay.textContent = g.label;
    pcOverlay.classList.toggle('on', solar.overlay > 0);
  }
  pcOverlay.addEventListener('click', () => {
    solar.overlay = (solar.overlay + 1) % GRID_STAGES.length;
    syncGridButton();
    applySun();
  });
  syncGridButton();
  pcContour.addEventListener('click', () => {
    solar.contour = solar.contour > 0.5 ? 0 : 1;
    pcContour.classList.toggle('on', solar.contour > 0.5);
    applySun();
  });

  /** How much of the hovered and the selected cell is drawn, the player's pick. */
  let cellMode: SubPlotMarkMode = 'fill';

  /**
   * How much of the hovered and the selected cell is drawn.
   *
   * The wash is what makes a pick obvious and also what hides the ground it is
   * drawn on, and the rim covers whatever the boundary runs through, so the
   * pair cycles through both, the rim alone, and neither. OFF changes the
   * DRAWING only: a double-click still picks a cell, the panel still names it
   * and ENTER PLOT still opens it. The two draw paths both take it, the mark
   * above the SURFACE edge and the plot mesh's own shader below.
   */
  const CELLS_KEY = 'atlas.cells.v1';
  const CELL_MODES: readonly SubPlotMarkMode[] = ['fill', 'edge', 'off'];
  const CELL_FACES: Record<SubPlotMarkMode, string> = {
    fill: 'CELLS: FILL + EDGE',
    edge: 'CELLS: EDGE',
    off: 'CELLS: OFF',
  };
  const pcCells = overlay.querySelector('#pc-cells') as HTMLButtonElement;
  function applyCellMode(): void {
    // `cells-mode-stuck` reaches the draw and leaves the button and the stored
    // pick alone, so the control is the build that had no control rather than
    // a second way to set one.
    const drawn: SubPlotMarkMode = deps.injects.state.cellsModeStuck ? 'fill' : cellMode;
    selectMark.setMode(drawn);
    hoverMark.setMode(drawn);
    deps.live.plot?.setCellMode(drawn);
    pcCells.textContent = CELL_FACES[cellMode];
    pcCells.classList.toggle('on', cellMode !== 'off');
    deps.live.needsRender = true;
  }
  try {
    const stored = localStorage.getItem(CELLS_KEY);
    if (stored === 'fill' || stored === 'edge' || stored === 'off') cellMode = stored;
  } catch { /* private mode */ }
  pcCells.addEventListener('click', () => {
    cellMode = CELL_MODES[(CELL_MODES.indexOf(cellMode) + 1) % CELL_MODES.length]!;
    try { localStorage.setItem(CELLS_KEY, cellMode); } catch { /* private mode */ }
    applyCellMode();
  });
  applyCellMode();

  /** Toggle a sweep. Clicking the active mode stops it. */
  function setPlaying(mode: 'sol' | 'year' | null): void {
    deps.viewClockHandle.state.playing = deps.viewClockHandle.state.playing === mode ? null : mode;
    pcPlaySol.classList.toggle('on', deps.viewClockHandle.state.playing === 'sol');
    pcPlayYear.classList.toggle('on', deps.viewClockHandle.state.playing === 'year');
    returnToLive();
  }
  pcPlaySol.addEventListener('click', () => setPlaying('sol'));
  pcPlayYear.addEventListener('click', () => setPlaying('year'));

  pcFly.addEventListener('click', () => {
    if (deps.cameraRig.cinema !== null) { deps.cameraRig.stopCinema(); return; }
    deps.cameraRig.cinema = 0;
    pcFly.classList.add('on');
    // The flight is only worth watching because the ground moves under it, so
    // the sol runs whether or not the player had it going.
    deps.viewClockHandle.state.playing = 'sol';
    pcPlaySol.classList.add('on');
    pcPlayYear.classList.remove('on');
  });
  syncPlotLabels();

  return {
    syncSolNote, syncPlotLabels, endSolScrub, endForecastScrub, applyCellMode, setPlaying,
    elPlotCtl, pcSol, pcLs, pcLsRow, pcFly, pcPlayYear,
    state: {
      get cellMode() { return cellMode; },
      set cellMode(v) { cellMode = v; },
    },
  };
}
